import axios from "axios";
import { encryptData, decryptData } from "./dataEncoder";
import {
  isRequestEncodingEnabled,
  isResponseDecodingEnabled,
  isDebugEnabled,
  shouldSkipRoute,
  shouldSkipMethod,
  shouldSkipContentType,
} from "../Config/encoding";
import { isTokenExpired, clearTokenData } from "./tokenValidator";
import { store } from "../Stores/store";
import { TOKEN_REFRESHED } from "../Stores/Auth/constants";

/** 401 here means wrong credentials, not an expired session — do not clear storage or hard-redirect. */
const ROUTES_SKIP_401_SESSION_CLEAR = [
  "employee/login",
  "user-employee/login",
  "family/login",
  "family/invite",
  "family/password",
];

const BACKEND_URI = process.env.REACT_APP_BACKEND_URI;

/**
 * No default timeout is set on the axios instance.
 * Long-running requests (e.g. document upload/processing) should pass
 * config.timeout in the request, e.g. .post(url, data, { timeout: 300000 }).
 */
const api = (token, cancelToken, isHandlerDisabled, isBlob) => {
  const axiosInstance = axios.create({
    baseURL: BACKEND_URI,
    headers: {
      // Custom headers (e.g. X-Client-Capabilities) must not be set here:
      // they fail CORS preflight on login from localhost. The dealer is
      // already treated as a safe client via its Mozilla User-Agent.
      ...(typeof token === "string"
        ? { Authorization: `Bearer ${token}` }
        : {}),
    },
    withCredentials: true,
    cancelToken,
    responseType: isBlob ? "blob" : "json",
  });

  // Request interceptor - Encode outgoing data
  if (!isHandlerDisabled) {
    axiosInstance.interceptors.request.use(
      (config) => {
        // Check token expiration before making request
        if (token && isTokenExpired()) {
          console.warn("⚠️  Token expired, clearing localStorage");
          clearTokenData();

          // Redirect to login
          if (typeof window !== "undefined") {
            window.location.href = "/";
          }

          return Promise.reject(new Error("Token expired"));
        }

        const encodingOn =
          isRequestEncodingEnabled() || isResponseDecodingEnabled();

        if (!encodingOn) {
          return config;
        }

        // Check if route should be skipped
        if (shouldSkipRoute(config.url)) {
          return config;
        }

        const isFormData = config.data instanceof FormData;
        const isFileBody =
          config.data instanceof Blob || config.data instanceof File;
        const contentType = config.headers?.["Content-Type"] || "";
        const skipBodyEncrypt =
          isBlob ||
          !config.data ||
          isFormData ||
          isFileBody ||
          shouldSkipContentType(contentType) ||
          !isRequestEncodingEnabled() ||
          shouldSkipMethod(config.method);

        if (!skipBodyEncrypt) {
          try {
            // Encrypt the data using AES-256-CBC
            const encryptedData = encryptData(config.data);

            config.data = {
              encrypted: true,
              data: encryptedData,
            };
          } catch (error) {
            console.error("❌ Failed to encrypt request data:", error);
            // If encryption fails, send original data
          }
        }

        // Capability header is independent of a JSON body so GET/DELETE and
        // multipart uploads still receive encrypted JSON responses.
        // Blob downloads must stay raw bytes.
        if (!isBlob && isResponseDecodingEnabled()) {
          config.headers = config.headers || {};
          config.headers["X-Data-Encrypted"] = "true";
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  if (!isHandlerDisabled)
    axiosInstance.interceptors.response.use(
      (response) => {
        // Check for token refresh from sliding window middleware (must run before any early returns)
        const newToken = response.headers?.["x-new-token"];
        const tokenRefreshed = response.headers?.["x-token-refreshed"];
        const newExpiresAt = response.headers?.["x-token-expires-at"];

        if (tokenRefreshed === "true" && newToken) {
          localStorage.setItem("token", newToken);
          if (newExpiresAt) {
            localStorage.setItem("token_expires_at", newExpiresAt);
          }
          store.dispatch({ type: TOKEN_REFRESHED, payload: newToken });
        }

        if (!isResponseDecodingEnabled()) {
          return response.data;
        }

        // Decrypt incoming data if it's encrypted
        if (!isBlob && response.data) {
          try {
            // Check if response has encrypted flag (matches Laravel format)
            if (
              response.data.encrypted === true &&
              response.data.data &&
              typeof response.data.data === "string"
            ) {
              try {
                // Decrypt the data using AES-256-CBC
                const decryptedData = decryptData(response.data.data);
                // console.log("🚀 ~ api ~ decryptedData:", decryptedData);

                response.data = decryptedData;
              } catch (decryptError) {
                console.error("❌ Decryption failed:", decryptError.message);
                console.error("💡 This usually means:");
                console.error(
                  "   1. Encryption keys don't match between frontend/backend",
                );
                console.error(
                  "   2. Backend is using different encryption format",
                );
                console.error(
                  "   3. REACT_APP_ENCRYPTION_KEY not set correctly",
                );
                console.error("");
                console.error(
                  "🔧 Quick fix: Set REACT_APP_ENCODING_ENABLED=false in .env",
                );
                console.error(
                  "   Or disable backend: ENABLE_DATA_ENCRYPTION=false",
                );

                // Return error to saga so it can handle gracefully
                throw new Error("Decryption failed - check encryption keys");
              }
            } else if (isDebugEnabled()) {
            }
          } catch (error) {
            console.error("❌ Response processing error:", error);
            // Re-throw to let saga error handling catch it
            throw error;
          }
        }

        if (
          response.data.status === "error" ||
          response.data.status === "failed"
        ) {
          let error;
          if (
            typeof response.data.message !== "string" &&
            Object.values(response.data.message).length
          )
            error = Object.values(response.data.message).join(", ");
          else error = response.data.message;
          if (
            typeof response.data.error !== "string" &&
            Object.values(response.data.error).length
          )
            error = Object.values(response.data.error).join(", ");
          /* eslint-disable-next-line prefer-destructuring */ else
            error = response.data.error;
          return Promise.reject(error);
        }
        return response.data;
      },
      (_error) => {
        const { response } = _error;
        if (response) {
          if (response.status === 401) {
            const reqUrl = response.config?.url ?? "";
            const skipSessionClear = ROUTES_SKIP_401_SESSION_CLEAR.some(
              (route) => reqUrl.includes(route),
            );
            if (!skipSessionClear) {
              localStorage.clear();
              // eslint-disable-next-line no-restricted-globals
              if (location) location.replace("/");
            }
          }

          // Decrypt error response if it's encrypted
          let errorData = response.data;
          if (
            errorData &&
            errorData.encrypted === true &&
            errorData.data &&
            typeof errorData.data === "string" &&
            isResponseDecodingEnabled()
          ) {
            try {
              errorData = decryptData(errorData.data);
              // Put it back on the response, exactly as the success path does.
              // Callers read e.response.data for the detail a failed request
              // carries — per-row import failures, field errors — and without
              // this they get the still-encrypted payload and lose the reason.
              response.data = errorData;
            } catch (decryptError) {
              console.error(
                "❌ Failed to decrypt error response:",
                decryptError,
              );
              // Continue with original encrypted data if decryption fails
            }
          }

          let error;
          if (
            typeof errorData?.message === "object" &&
            Object.values(errorData?.message).length
          )
            error = Object.values(errorData?.message).join(", ");
          else error = errorData?.message;
          if (
            !error &&
            typeof errorData?.error === "object" &&
            Object.values(errorData?.error).length
          )
            error = Object.values(errorData?.error).join(", ");
          else if (!error) error = errorData?.error;
          return Promise.reject({ ..._error, error });
        }
        return Promise.reject(_error);
      },
    );
  return axiosInstance;
};

export default api;
